import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

// A lightweight global registry of the currently-open LayoverModals, in mount
// order. Each layover registers itself so it can learn its depth (for the
// depth-shift + single dim) and so any layer can close the whole stack.

// Initial and minimum width of the resizable layover panels.
export const DEFAULT_LAYOVER_WIDTH = 768

interface IRegistered {
  id: number
  requestClose: () => void
  // True once this layer started its exit transition (but before it unmounts),
  // so the layers beneath can shift back in sync with its slide-out.
  closing: boolean
}

interface ILayoverStack {
  register: (id: number, requestClose: () => void) => void
  unregister: (id: number) => void
  // Flag a layer as closing so the depth-shift recomputes immediately, rather
  // than waiting for the slide-out to finish and the layer to unmount.
  markClosing: (id: number) => void
  // Monotonic id source so a layover's index reflects its mount order.
  nextId: () => number
  stack: IRegistered[]
  // Close every open layover (top-first), each playing its own exit transition.
  closeAll: () => void
  // Shared panel width — resizing one layer resizes the whole stack, and a layer
  // opening on top inherits the current size. Resets when the stack empties.
  width: number
  setWidth: (width: number) => void
  // True while the resize handle is being dragged. Width changes from stacking a
  // wider layer ease in; while dragging, panels must track the cursor instantly,
  // so layers read this to switch their width transition off.
  isResizing: boolean
  setResizing: (resizing: boolean) => void
}

const LayoverStackContext = createContext<ILayoverStack | undefined>(undefined)

const LayoverStackProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [stack, setStack] = useState<IRegistered[]>([])
  const [width, setWidth] = useState(DEFAULT_LAYOVER_WIDTH)
  const [isResizing, setResizing] = useState(false)
  const idCounter = useRef(0)

  // Start each fresh stack at the default size once everything has closed.
  useEffect(() => {
    if (stack.length === 0) setWidth(DEFAULT_LAYOVER_WIDTH)
  }, [stack.length])

  const nextId = useCallback(() => {
    idCounter.current += 1
    return idCounter.current
  }, [])

  const register = useCallback((id: number, requestClose: () => void) => {
    setStack((prev) =>
      prev.some((l) => l.id === id)
        ? prev
        : [...prev, { id, requestClose, closing: false }]
    )
  }, [])

  const unregister = useCallback((id: number) => {
    setStack((prev) =>
      prev.some((l) => l.id === id) ? prev.filter((l) => l.id !== id) : prev
    )
  }, [])

  const markClosing = useCallback((id: number) => {
    setStack((prev) =>
      prev.map((l) => (l.id === id ? { ...l, closing: true } : l))
    )
  }, [])

  // Mirror the latest stack into a ref so closeAll stays a stable callback
  // (no stack dependency) and never closes over a stale list.
  const stackRef = useRef(stack)
  stackRef.current = stack

  const closeAll = useCallback(() => {
    // requestClose is idempotent (guarded in LayoverModal), so closing an
    // already-closing layer is a no-op. Top-first reads naturally.
    ;[...stackRef.current].reverse().forEach((l) => l.requestClose())
  }, [])

  const value = useMemo(
    () => ({
      register,
      unregister,
      markClosing,
      nextId,
      stack,
      closeAll,
      width,
      setWidth,
      isResizing,
      setResizing,
    }),
    [
      register,
      unregister,
      markClosing,
      nextId,
      stack,
      closeAll,
      width,
      isResizing,
    ]
  )

  return (
    <LayoverStackContext.Provider value={value}>
      {children}
    </LayoverStackContext.Provider>
  )
}

// Returns undefined when no provider is mounted, so LayoverModal degrades
// gracefully to single-layer behaviour (e.g. in isolated tests).
const useLayoverStack = () => useContext(LayoverStackContext)

export { LayoverStackProvider, useLayoverStack }
